import React from 'react';
import PropTypes from 'prop-types';
import { Button, Select, message } from 'antd';
import { POLARITY_TYPE__A, POLARITY_TYPE__B, POLARITY_TYPE__C } from '@shared/constants/constants';
import { generateTypeAPolarity, generateTypeBPolarity, generateTypeCPolarity } from '../../../lib/cable';
import { Api } from '../../../lib/api';
import './PolarityPage.scss';
import CreatePolarityModal from '../components/CreatePolarityModal';

export class PolarityPage extends React.PureComponent {
    static propTypes = {
        wizard: PropTypes.object,
        updateWizardData: PropTypes.func
    };
    state = {
        polarityTypes: [],
        visible: false,
        numFibers: this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly,
        polarityType: null,
        name: null,
        mapping: Array(12)
            .fill('')
            .map((v, i) => i + 1),
        tableIndex: 0
    };

    updatePolarities = async () => {
        return Api.getPolaritiesByFiberCount(this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly).then(
            async (polarityTypes) => {
                let { name, mapping, id } = this.props.wizard?.assembly?.polarity;

                // if test plan has non default polarity, check if it exists in the db
                if (id && id !== POLARITY_TYPE__A && id !== POLARITY_TYPE__B && id !== POLARITY_TYPE__C) {
                    let polarity = polarityTypes.find((v) => v.id === id || v.name === name); // name is unique

                    // polarity not in db but is in the wizard so create new polarity
                    if (!polarity) {
                        try {
                            let result = await Api.createPolarity(name, mapping.length, mapping.toString());

                            this.props.updateWizardData((state) => ({
                                assembly: {
                                    ...state.assembly,
                                    polarity: {
                                        id: result.id,
                                        name,
                                        mapping
                                    }
                                }
                            }));

                            polarityTypes.push(result);
                        } catch (err) {
                            // Do nothing, keep polarity the same as before
                            console.warn(`Polarity ${id} found in test plan but not in database, polarity auto creation failed.`, err);
                        }
                    }

                    // polarity in db and is different from the one in the wizard
                    // can be caused if the user open wizards, polarity tab auto creates polarity,
                    // then user leaves wizard page, then revisits wizard page
                    if (polarity && polarity.id !== id) {
                        this.props.updateWizardData((state) => ({
                            assembly: {
                                ...state.assembly,
                                polarity: {
                                    id: polarity.id,
                                    name: polarity.name,
                                    mapping: polarity.polarity.split(',').map((v) => (v === '-' ? 0 : parseInt(v)))
                                }
                            }
                        }));
                    }
                }

                this.setState({ polarityTypes });
            }
        );
    }

    updatePolaritiesFromModal = async (polarity) => {
        this.updatePolarities().then(() => {
            this.selectMapping(polarity.id);
        });
    }

    componentDidMount() {
        this.updatePolarities();
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.numFibers !== prevState.numFibers && this.state.mapping.length < this.state.numFibers) {
            // Copy array
            let mapping = [...this.state.mapping];

            // Extend the array
            mapping.length = this.state.numFibers;

            // Fill in the new section with empty strings
            mapping.fill('', this.state.mapping.length);

            this.setState({ mapping });
        }

        if(this.state.numFibers !== this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly) {
            this.updatePolarities();
            this.setState({
                numFibers: this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly
            })
        }
    }

    showModal = () => {
        this.setState({
            numFibers: this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly,
            polarityType: null,
            name: null,
            mapping: Array(this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly)
                .fill('')
                .map((v, i) => i + 1),

            visible: true
        });
    };

    selectMapping = (value) => {
        if (value === POLARITY_TYPE__A) {
            this.props.updateWizardData((state) => ({
                assembly: {
                    ...state.assembly,
                    polarity: {
                        id: value,
                        name: null,
                        mapping: generateTypeAPolarity(
                            this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly
                        )
                    }
                }
            }));
        } else if (value === POLARITY_TYPE__B) {
            this.props.updateWizardData((state) => ({
                assembly: {
                    ...state.assembly,
                    polarity: {
                        id: value,
                        name: null,
                        mapping: generateTypeBPolarity(
                            this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly
                        )
                    }
                }
            }));
        } else if (value === POLARITY_TYPE__C) {
            this.props.updateWizardData((state) => ({
                assembly: {
                    ...state.assembly,
                    polarity: {
                        id: value,
                        name: null,
                        mapping: generateTypeCPolarity(
                            this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly
                        )
                    }
                }
            }));
        } else {
            let mapping = this.state.polarityTypes.find((polarity) => polarity.id === value);
            let polarityMapping = mapping.polarity.split(',').map((v) => (v === '-' ? 0 : parseInt(v)));

            let darkChannels = [];
            for (let i = 0; i < polarityMapping.length; i++) {
                if (polarityMapping[i] === 0) {
                    darkChannels.push(i);
                }
            }

            if (mapping) {
                this.props.updateWizardData((state) => ({
                    assembly: {
                        ...state.assembly,
                        polarity: {
                            id: value,
                            name: mapping.name,
                            mapping: polarityMapping,
                            darkChannels: darkChannels
                        }
                    }
                }));
                if (darkChannels.length !== 0) {
                    message.info(
                        'This mapping contains dark fibers, they will be applied to the IL/RL test automatically',
                        3
                    );
                }
            }
        }
    }
    closePolarityModal = () => {
        this.setState({
            visible: false
        })
    }

    render() {
        let numRows = Math.ceil((this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly) / 12);
        return (
            <React.Fragment>
                <div className="page polarity-page">
                    <div className="section pt1">
                        {' '}
                        <div className="title">Cable Mapping</div>
                        {this.state.visible && (<CreatePolarityModal
                            name={this.state.name}
                            mapping={this.state.mapping}
                            numFibers={this.state.numFibers}
                            polarityType={this.state.polarityType}
                            tableIndex={this.state.tableIndex}
                            close={this.closePolarityModal}
                            isOpen={this.state.visible}
                            numRows={numRows}
                            updatePolarities={this.updatePolaritiesFromModal}
                        >
                        </CreatePolarityModal>)}
                        <Select
                            value={this.props.wizard.assembly.polarity.id}
                            onChange={this.selectMapping}
                        >
                            <Select.Option value={POLARITY_TYPE__A}>Default</Select.Option>
                            {this.state.polarityTypes
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((polarity) => (
                                    <Select.Option key={polarity.id} value={polarity.id}>
                                        {polarity.name}
                                    </Select.Option>
                                ))}
                        </Select>
                        <Button onClick={this.showModal} style={{ marginLeft: '12px' }}>
                            Create New Polarity
                        </Button>
                        <div className="cable-mapping-table">
                            {
                                // Add 12 empty cells
                                //[...Array(12)].map((value, index) => <div key={'header-' + index} />)
                            }
                            {
                                // For each row
                                [...Array(numRows)].map((v1, rowIndex) => (
                                    <React.Fragment key={'row-' + rowIndex}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                fontStyle: 'italic',
                                                fontWeight: '500'
                                            }}
                                        >
                                            <div>
                                                Output Fibers ({12 * rowIndex + 1}-{12 * rowIndex + 12})
                                            </div>
                                            <hr style={{ width: '100%' }} />
                                            <div style={{ color: 'blue' }}>
                                                Input Fibers ({12 * rowIndex + 1}-{12 * rowIndex + 12})
                                            </div>
                                        </div>
                                        {
                                            // For each column
                                            //[...Array(rowIndex + 1 < numRows ? 12 : this.props.fibers - (numRows - 1) * 12)].map((v2, columnIndex) =>
                                            [...Array(12)].map((v2, columnIndex) => (
                                                <div
                                                    key={'cell-' + (rowIndex * 12 + columnIndex)}
                                                    style={{ display: 'flex', flexDirection: 'column' }}
                                                >
                                                    {this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly <
                                                    12 * rowIndex + 1 + columnIndex ? (
                                                        <div />
                                                    ) : (
                                                        <div>{12 * rowIndex + 1 + columnIndex}</div>
                                                    )}

                                                    {this.props.wizard.fibersPerCable * this.props.wizard.cablesPerAssembly >=
                                                        12 * rowIndex + 1 + columnIndex && <hr style={{ width: '100%' }} />}
                                                    <div style={{ color: 'blue' }}>
                                                        {
                                                            this.props.wizard.assembly.polarity.mapping[
                                                                rowIndex * 12 + columnIndex
                                                            ]
                                                        }
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </React.Fragment>
                                ))
                            }
                        </div>
                        <div>*Output fibers will correspond to the Switch channels set in IL/RL</div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}
